import {Box, Button} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {
    getGridNumericOperators,
    getGridSingleSelectOperators,
    getGridStringOperators,
} from "@mui/x-data-grid-premium";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {SUPPLIER_MATCH_BASIS_LABEL} from "~/pages/supplier-360/constants/supplierEntityLabels";
import {
    SUPPLIER_CLUSTER_MEMBER_MATCH_BASES,
    type SupplierClusterMember,
    type SupplierDataSource,
} from "~/pages/supplier-360/types/Supplier360Types";
import {SupplierCountryFlag} from "~/pages/supplier-360/components/SupplierCountryFlag";
import {CountryColumnHeader} from "~/pages/supplier-360/components/CountryColumnHeader";
import {Tag} from "~/components/tag/Tag";

/**
 * Column `field` → DRF ordering key, for the columns whose sort key is a nested lookup (§2.2).
 * The rest (`s_name`, `vat`, `spend`, `match_basis`) sort under their own field name.
 */
export const SUPPLIER_RAW_ORDERING_FIELDS: Partial<Record<string, string>> = {
    data_source: 'data_source__name',
    country: 'country__code_a2',
};

const VAT_OPERATORS = getGridStringOperators().filter(o => o.value === 'equals' || o.value === 'contains');
const EXACT_OR_ANY_OF_OPERATORS = getGridStringOperators().filter(
    o => o.value === 'equals' || o.value === 'isAnyOf',
);
const SELECT_OPERATORS = getGridSingleSelectOperators().filter(o => o.value === 'is' || o.value === 'isAnyOf');
const RANGE_FILTER_OPERATORS = getGridNumericOperators().filter(o => o.value === '>=' || o.value === '<=');

const MATCH_BASIS_OPTIONS = SUPPLIER_CLUSTER_MEMBER_MATCH_BASES.map(value => ({
    value,
    label: SUPPLIER_MATCH_BASIS_LABEL[value],
}));

/** `name` is the display label, `code` the stable identifier it falls back to (docs §2.24). */
function dataSourceLabel(source: SupplierDataSource | null | undefined): string {
    return source?.name || source?.code || '';
}

/**
 * Live ERP name (`s_name`), with the frozen cluster-time name alongside in parentheses when it's
 * known AND different — so a changed name downstream is visible without losing what this row was
 * matched under, without showing the same string twice for the common case where neither has
 * changed. Falls back to `normalized_name` alone when there's no live match (e.g. a manually-added
 * row's synthetic id has no `data_source.Supplier` to join against), docs §2.2.
 */
function supplierDisplayName(row: SupplierClusterMember): string {
    if (!row.s_name) return row.normalized_name;
    if (!row.normalized_name || row.normalized_name === row.s_name) return row.s_name;
    return `${row.s_name} (${row.normalized_name})`;
}

export function getSupplierRawColumns(
    onOpenEntity: (entityId: number) => void,
    dataSources: SupplierDataSource[],
): GridColDef<SupplierClusterMember>[] {
    return [
        {
            field: 's_name',
            headerName: 'Supplier name',
            flex: 1.5,
            minWidth: 220,
            filterable: false,
            valueGetter: (_value, row) => supplierDisplayName(row),
            renderCell: ({row}) => <Tag label={supplierDisplayName(row)} color="default" variant="outlined" />,
        },
        {
            field: 'data_source',
            headerName: 'Source',
            type: 'singleSelect',
            // Filtered by `DataSource` id (§2.2), so the options are the `data/source/` rows (§2.24).
            valueOptions: dataSources.map(source => ({value: source.id, label: dataSourceLabel(source)})),
            flex: 0.8,
            minWidth: 130,
            filterOperators: SELECT_OPERATORS,
            // Nested `DataSource` since CAT-2460 (docs §2.2/§2.24).
            valueGetter: (_value, row) => row.data_source?.id ?? null,
            renderCell: ({row}) => dataSourceLabel(row.data_source) || '—',
        },
        {
            field: 'country',
            headerName: 'Country',
            flex: 0.5,
            minWidth: 120,
            align: 'center',
            renderHeader: () => <CountryColumnHeader/>,
            // ISO alpha-2 codes — `country__code_a2` exact or `__in` (§2.2).
            filterOperators: EXACT_OR_ANY_OF_OPERATORS,
            valueGetter: (_value, row) => row.country?.code_a2 ?? '',
            renderCell: ({row}) => <SupplierCountryFlag country={row.country}/>,
        },
        {
            field: 'vat',
            headerName: 'VAT',
            flex: 0.8,
            minWidth: 140,
            filterOperators: VAT_OPERATORS,
            renderCell: ({row}) => row.vat || '—',
        },
        {
            field: 'spend',
            headerName: 'Spend',
            type: 'number',
            headerAlign: 'left',
            flex: 0.8,
            minWidth: 120,
            filterOperators: RANGE_FILTER_OPERATORS,
            // Computed per raw row (docs §2.2) — coerced because a DRF decimal can arrive as a string.
            valueGetter: (_value, row) => Number(row.spend ?? 0),
            renderCell: ({value}) => <CurrencyComponent v={value} nDigits={3}/>,
        },
        {
            field: 'match_basis',
            headerName: 'Match basis',
            type: 'singleSelect',
            valueOptions: MATCH_BASIS_OPTIONS,
            flex: 0.8,
            minWidth: 130,
            filterOperators: SELECT_OPERATORS,
        },
        {
            field: 'entity',
            headerName: 'Resolves to',
            flex: 1,
            minWidth: 200,
            sortable: false,
            filterable: false,
            // Nested entity since 2026-09-08 (docs §3.2) — the name is on the row already.
            valueGetter: (_value, row) => row.entity?.canonical_name ?? '',
            renderCell: ({row}) => <Button
                size="small"
                variant="text"
                endIcon={<ArrowForwardIcon fontSize="small"/>}
                onClick={() => onOpenEntity(row.entity.id)}
                title={row.entity?.canonical_name || undefined}
                sx={{maxWidth: '100%', minWidth: 0, '& .MuiButton-endIcon': {flexShrink: 0}}}>
                <Box component="span" sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {row.entity?.canonical_name || `Supplier #${row.entity?.id}`}
                </Box>
            </Button>,
        },
    ];
}
